import { useNavigate } from 'react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeetingList } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import { AppScreen, BadgeTone, Button, CardGrid, MeetingCard, PageIntro } from 'components/ds';
import styles from './ScheduleSelectMeeting.module.css';

export const ScheduleSelectMeeting = () => {
    const navigate = useNavigate();
    const { data } = useMeetingList();
    const { t } = useTranslation();

    // 컴포넌트 마운트 시 모임 목록 조회
    useEffect(() => {
        meetingStore.onGetMeetingList();
    }, []);

    const allMeetings = data?.data || [];

    // ★ 이미 지난 모임은 일정 조율 대상에서 제외 (사용자 요구).
    //   기준: schedule.endDatetime (또는 startDatetime fallback) 이 현재보다 이전이면 제외.
    //   schedule 자체가 없는 모임 (일정 미정) 은 그대로 표시 — 조율 대상 본질.
    //   ended_at NOT NULL (creator 종료) 또는 paymentSettledAt NOT NULL (정산 완료) 도 제외.
    const nowMs = Date.now();
    const meetings = allMeetings.filter((m: any) => {
        if (m.endedAt) return false;          // 주최자 명시 종료 — 조율 의미 없음
        if (m.paymentSettledAt) return false; // 정산 완료 — 종결된 모임
        const endRaw = m.schedule?.endDatetime || m.scheduleEndDateTime || m.schedule?.startDatetime || m.scheduleStartDateTime;
        if (!endRaw) return true;             // 일정 미정 — 조율 대상
        const t = new Date(endRaw).getTime();
        if (isNaN(t)) return true;
        return t >= nowMs;                    // 종료가 현재 이전이면 제외 (이미 지난 모임)
    });

    const handleMeetingClick = (meetingId: string) => {
        navigate(`/schedule/main/${meetingId}`);
    };

    return (
        <AppScreen title={t('scheduleSelect.title')} bar="sub">
            {meetings.length === 0 ? (
                <div className={styles.empty}>
                    <span className={styles.emptyTitle}>{t('scheduleSelect.empty')}</span>
                    <span className={styles.emptyDesc}>{t('scheduleSelect.emptyDesc')}</span>
                    <div className={styles.emptyAction}>
                        <Button variant="primary" size="lg" block onClick={() => navigate('/meeting/create')}>
                            {t('scheduleSelect.createMeeting')}
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <PageIntro title={t('scheduleSelect.selectGuide')} />
                    <CardGrid>
                        {meetings.map((meeting: any) => (
                            <MeetingCard
                                key={meeting.meetingId}
                                name={meeting.meetingName}
                                when={t('scheduleSelect.hostMeta', {
                                    name: meeting.creatorNickname
                                        ? decodeURIComponent(meeting.creatorNickname)
                                        : t('scheduleSelect.unknown'),
                                    count: meeting.currentMemberCount || 0,
                                })}
                                badges={meeting.isRegularMeeting
                                    ? [{ label: t('scheduleSelect.regular'), tone: 'neutral' as BadgeTone }]
                                    : []}
                                onClick={() => handleMeetingClick(meeting.meetingId)}
                            />
                        ))}
                    </CardGrid>
                </>
            )}
        </AppScreen>
    );
};
